import { useState, useCallback, useEffect } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { getLayerByCustomId } from '@utils/map/helpers';

/** Default starting z-index for layer groups */
const BASE_Z_INDEX = 10;

/**
 * @typedef {Object} LayerItem
 * @property {string} key - Unique identifier for the layer (used for React keys and ordering)
 * @property {string} [layerId] - Actual OpenLayers customLayerId (defaults to key if not provided)
 * @property {React.ReactNode} component - React component to render for this layer
 */

/**
 * @typedef {Object} UseLayerReorderReturn
 * @property {string[]} layerOrder - Current order of layer keys
 * @property {Function} setLayerOrder - Setter for layer order
 * @property {Function} handleReorder - Handler for drag-end reorder events
 * @property {Function} getOrderedLayers - Returns layers sorted by current order
 * @property {Function} updateZIndices - Manually trigger z-index recalculation
 * @property {Function} isLayerReady - Check if a layer exists on the map
 */

/**
 * Hook for managing layer reordering with dynamic z-index updates.
 *
 * Handles the complexity of mapping between:
 * - Layer keys (used for React rendering and state)
 * - Layer IDs (actual OpenLayers customLayerId on the map)
 *
 * Listens to OpenLayers layer add/remove events to detect when layers become ready.
 *
 * @param {Object} mapObject - OpenLayers map instance
 * @param {LayerItem[]} initialLayers - Array of layer objects with key, optional layerId, and component
 * @param {number} [baseZIndex=10] - Starting z-index for this layer group
 * @returns {UseLayerReorderReturn} Layer order state and handlers
 */
export const useLayerReorder = (mapObject, initialLayers = [], baseZIndex = BASE_Z_INDEX) => {
	const [layerOrder, setLayerOrder] = useState([]);
	const [readyLayerIds, setReadyLayerIds] = useState(new Set());

	/**
	 * Builds a lookup map from layer key to actual OpenLayers layer ID.
	 * This allows layers to use different keys for React (e.g., 'contours')
	 * vs OpenLayers (e.g., 'contoursLayer').
	 */
	const getLayerIdMap = useCallback(() => {
		const map = {};
		initialLayers.forEach(layer => {
			map[layer.key] = layer.layerId || layer.key;
		});
		return map;
	}, [initialLayers]);

	/**
	 * Scans the map for which layers are currently added and updates readyLayerIds.
	 * Only triggers a state update if the set of ready layers has actually changed.
	 */
	const updateReadyLayers = useCallback(() => {
		if (!mapObject) return;

		const layerIdMap = getLayerIdMap();
		const newReadyIds = new Set();

		initialLayers.forEach(layer => {
			const actualLayerId = layerIdMap[layer.key] || layer.key;
			if (getLayerByCustomId(mapObject, actualLayerId)) {
				newReadyIds.add(layer.key);
			}
		});

		setReadyLayerIds(prev => {
			const hasChanged =
				prev.size !== newReadyIds.size || ![...prev].every(id => newReadyIds.has(id));
			return hasChanged ? newReadyIds : prev;
		});
	}, [mapObject, initialLayers, getLayerIdMap]);

	/**
	 * Reconcile layerOrder when initialLayers changes.
	 *
	 * This effect handles three scenarios:
	 * 1. Initial mount: Set layerOrder to match initialLayers order
	 * 2. Layers added: Append new layers to the end (preserving user's custom order)
	 * 3. Layers removed: Filter out keys that no longer exist
	 *
	 * Why reconciliation instead of one-time init?
	 * - initialLayers can change after mount (e.g., new analysis layer becomes available)
	 * - Without reconciliation, new layers would never appear in the sortable list
	 * - User's drag-and-drop order must be preserved when layers are added/removed
	 *
	 * Why the equality check at the end?
	 * - initialLayers reference may change even when content is the same (upstream memoization)
	 * - Without equality check, we'd create new layerOrder array on every such change
	 * - This would trigger unnecessary re-renders in SortableLayerGroup and children
	 */
	useEffect(() => {
		// Handle empty initialLayers - reset to empty array
		if (initialLayers.length === 0) {
			setLayerOrder(prev => (prev.length === 0 ? prev : []));
			return;
		}

		setLayerOrder(prev => {
			const keys = initialLayers.map(layer => layer.key);

			// First time initialization - use initialLayers order as-is
			if (prev.length === 0) return keys;

			// Reconcile: preserve user's order for existing layers, append new ones
			const keySet = new Set(keys);
			const merged = prev.filter(key => keySet.has(key)); // Remove deleted layers
			const mergedSet = new Set(merged);
			keys.forEach(key => {
				if (!mergedSet.has(key)) merged.push(key); // Append new layers
			});

			// Avoid unnecessary state update if nothing actually changed
			// This prevents re-renders when initialLayers reference changes but content is same
			const isEqual =
				merged.length === prev.length && merged.every((key, i) => key === prev[i]);

			return isEqual ? prev : merged;
		});
	}, [initialLayers]);

	// Subscribe to OpenLayers layer collection events to detect when layers are added/removed
	useEffect(() => {
		if (!mapObject) return;

		updateReadyLayers();

		const layers = mapObject.getLayers();
		const handleLayerChange = () => updateReadyLayers();

		layers.on('add', handleLayerChange);
		layers.on('remove', handleLayerChange);

		return () => {
			layers.un('add', handleLayerChange);
			layers.un('remove', handleLayerChange);
		};
	}, [mapObject, updateReadyLayers]);

	/**
	 * Applies z-index values to OpenLayers layers based on their position in the order array.
	 * Layers at the TOP of the list (index 0) get the HIGHEST z-index to render on top.
	 *
	 * @param {string[]} order - Array of layer keys in display order
	 */
	const updateZIndices = useCallback(
		order => {
			if (!mapObject) return;

			const layerIdMap = getLayerIdMap();
			const totalLayers = order.length;

			order.forEach((layerKey, index) => {
				const actualLayerId = layerIdMap[layerKey] || layerKey;
				const layer = getLayerByCustomId(mapObject, actualLayerId);
				if (layer) {
					// Invert index: first item (index 0) gets highest z-index
					const zIndex = baseZIndex + (totalLayers - 1 - index);
					layer.setZIndex(zIndex);
				}
			});
		},
		[mapObject, baseZIndex, getLayerIdMap]
	);

	// Re-apply z-indices whenever the set of ready layers changes
	useEffect(() => {
		if (layerOrder.length > 0 && readyLayerIds.size > 0) {
			updateZIndices(layerOrder);
		}
	}, [readyLayerIds, layerOrder, updateZIndices]);

	/**
	 * Handles drag-end event from dnd-kit. Reorders layers and updates z-indices.
	 *
	 * @param {string} activeId - Key of the layer being dragged
	 * @param {string} overId - Key of the layer being dropped onto
	 */
	const handleReorder = useCallback(
		(activeId, overId) => {
			if (activeId === overId) return;

			setLayerOrder(prev => {
				const oldIndex = prev.indexOf(activeId);
				const newIndex = prev.indexOf(overId);

				if (oldIndex === -1 || newIndex === -1) return prev;

				const newOrder = arrayMove(prev, oldIndex, newIndex);
				updateZIndices(newOrder);
				return newOrder;
			});
		},
		[updateZIndices]
	);

	/**
	 * Returns layers sorted according to current layerOrder.
	 *
	 * @param {LayerItem[]} layers - Array of layer objects
	 * @returns {LayerItem[]} Sorted array of layer objects
	 */
	const getOrderedLayers = useCallback(
		layers => {
			if (layerOrder.length === 0) return layers;

			return layerOrder.map(key => layers.find(layer => layer.key === key)).filter(Boolean);
		},
		[layerOrder]
	);

	/**
	 * Checks if a layer is currently added to the OpenLayers map.
	 *
	 * @param {LayerItem} layer - Layer object to check
	 * @returns {boolean} True if the layer exists on the map
	 */
	const isLayerReady = useCallback(
		layer => {
			return readyLayerIds.has(layer.key);
		},
		[readyLayerIds]
	);

	return {
		layerOrder,
		setLayerOrder,
		handleReorder,
		getOrderedLayers,
		updateZIndices,
		isLayerReady,
	};
};

export default useLayerReorder;
